import { SortDirection } from "@/api/shared";
import { UsersApi } from "@/api/users";
import { Modal } from "@/components";
import { useDebouncedListSearch, useDownloadFile } from "@/hooks";
import { User } from "@/types";
import { ExtractKeys, LocalStorageKeys, ResourceName } from "@/utils";
import { Close, Download, ExpandMore, Search } from "@mui/icons-material";
import {
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
} from "@mui/material";
import { useIsFetching } from "@tanstack/react-query";
import { FC, useState } from "react";
import { TopToolbar as RaTopToolbar, useListContext } from "react-admin";
import { UsersFilter } from "../../modals";
import { FiltersList } from "./filters-list.component";

type SortField = ExtractKeys<User, "firstName" | "createdAt" | "lastSeen">;

const sortFieldToColName = (field: SortField) => {
  switch (field) {
    case "firstName":
      return "Name";
    case "createdAt":
      return "Member since";
    case "lastSeen":
      return "Last active";
  }
};

const TopToolbar: FC = () => {
  const { filterValues, sort } = useListContext();
  const [searchQuery, setSearchQuery] = useDebouncedListSearch();

  const { isLoading: isCsvDownloading, download: downloadCsv } =
    useDownloadFile({
      apiFunc: UsersApi.downloadCsv,
      localStorageFileKey: LocalStorageKeys.usersCsvReport,
      filterValues,
      sort: {
        sortBy: sort.field,
        sortDirection: sort.order.toLowerCase() as SortDirection,
      },
    });
  const isUsersFetching = useIsFetching({
    queryKey: [ResourceName.USERS, "getList"],
  });

  const [isFiltersModalOpened, setIsFiltersModalOpened] = useState(false);

  return (
    <>
      {/* Modal used here due to RA listContext */}
      <Modal
        title="Filters"
        isOpened={isFiltersModalOpened}
        onClose={() => setIsFiltersModalOpened(false)}
        children={
          <UsersFilter onModalClose={() => setIsFiltersModalOpened(false)} />
        }
      />

      <RaTopToolbar
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          marginTop: 1,
          paddingRight: 0,
        }}
      >
        <Stack
          direction={"row"}
          sx={{ width: "100%", justifyContent: "space-between" }}
        >
          <Chip
            size="small"
            variant="filled"
            label={`Sorted by: ${sortFieldToColName(sort.field as SortField)}`}
          />

          <Stack direction={"row"} spacing={1}>
            <TextField
              size="small"
              variant="outlined"
              placeholder="Search by user"
              sx={{ width: "220px" }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              slotProps={{ htmlInput: { maxLength: 50 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {!!isUsersFetching && <CircularProgress size={16} />}
                    {!isUsersFetching && !!searchQuery && (
                      <Close
                        sx={{ cursor: "pointer" }}
                        onClick={() => setSearchQuery("")}
                      />
                    )}
                  </InputAdornment>
                ),
              }}
            />
            <Button
              size="small"
              endIcon={<ExpandMore />}
              sx={{ minWidth: "auto" }}
              onClick={() => setIsFiltersModalOpened(true)}
            >
              Filters
            </Button>
            <Button
              size="small"
              endIcon={<Download />}
              sx={{ minWidth: "auto" }}
              loading={isCsvDownloading}
              disabled={isCsvDownloading}
              onClick={downloadCsv}
            >
              Download CSV
            </Button>
          </Stack>
        </Stack>

        <FiltersList />
      </RaTopToolbar>
    </>
  );
};

export { TopToolbar };
